<template>
  <div>
    <b-modal v-model="copying" id="copy-model" title="Copy Model" centered no-close-on-backdrop ok-only ok-title="Cancel" ok-variant="secondary" @ok="closeCopy()" @close="closeCopy()">
      <!-- <label for="pcnCopySearchBox">Enter Catalog Number</label> -->
      <b-form-group id="copy-group" label="Enter Catalog Number" label-for="pcnCopySearchBox" :state="copyPcnState" :invalid-feedback="invalidFeedback">
        <b-input-group>
          <b-form-input autofocus id="pcnCopySearchBox" v-model="searchPcn" :state="copyPcnState" @keyup.enter.native="copyClick()"></b-form-input>
          <template v-slot:append>
            <b-button variant="primary" :disabled="searchPcn.length === 0" @click="copyClick()">Copy</b-button>
          </template>
        </b-input-group>
        <b-form-checkbox id="pcnCopyModelChk" v-model="copyModelNum" size="sm">
          Copy Model Number?
        </b-form-checkbox>
      </b-form-group>
    </b-modal>
    <b-modal v-model="editing" id="model-details" size="xl" centered scrollable no-close-on-backdrop @close="closeEdit" @cancel="closeEdit" title-class="w-100">
      <template v-slot:modal-title>
        <div class="float-left">Details for model: {{ (activeModel && !activeModel.loading) ? activeModel.PcnCatalogNumber : '' }}</div>
        <b-badge variant="warning" pill v-if="changed" id="popover-model-modified">
          <b-icon icon="exclamation-triangle-fill" variant="light" aria-hidden="true"></b-icon>
          <b-popover target="popover-model-modified" triggers="hover" variant="info" placement="bottom">
            <template v-slot:title>
              This model has been modified. Changes are not yet saved.
            </template>
          </b-popover>
        </b-badge>
        <b-button-toolbar class="ml-3 float-left" v-if="activeModel && !activeModel.loading">
          <b-button-group size="sm" class="mx-12">
            <b-button size="sm" variant="link" v-b-modal.modalDeleteConfirm v-if="!activeModel.Created">
              <b-icon icon="trash" />
            </b-button>
          </b-button-group>
          <b-button-group size="sm" class="mx-12">
            <b-button size="sm" variant="link" v-b-modal.modalSAP class="">
              <!-- [SAP] -->
            </b-button>
          </b-button-group>
        </b-button-toolbar>
        <b-button-toolbar class="float-left float-md-right" v-if="activeModel && !activeModel.loading">
          <b-button-group size="sm" class="mx-12">
            <b-button size="sm" @click="editModel" v-if="!activeModel.Created && !editingEntireModel" variant="primary">
              Edit All Fields
              <b-icon icon="pencil-square" class="ml-1"/>
            </b-button>
          </b-button-group>
        </b-button-toolbar>

        <b-modal id="modalDeleteConfirm" title="Confirm Deletion" size="sm">
          <center>
            <p class="my-4">Do you want to Submit a Deletion Request for this Model?</p>
          </center>
          <b-form-textarea v-model="comment" placeholder="Enter a optional Comment" rows="3" max-rows="6" autofocus>
          </b-form-textarea>
          <template #modal-footer>
            <b-button size="sm" variant="danger" @click="deleteModelInternal()">
              OK
            </b-button>
          </template>
        </b-modal>
        <b-modal id="modalSAP" title="SAP" size="sm">
          <center>
            <p class="my-4">
              TODO: SAP data
            </p>
          </center>
        </b-modal>
      </template>
      <b-form>
        <editor-editor
          :obj="activeModel"
          :editor="editorLookup.model"
          @changed="onChanged"
          @validated="onValidated"
          @sectionMounted="sectionMounted"
          :readOnly="readOnly"
          @save="saveChanges"
          :cancel="cancelEditSection"
        />
      </b-form>
      <template v-slot:modal-footer="{ cancel }">
        <b-button size="sm" variant="primary" @click="saveOrUpdateModel()" v-if="!readOnly">
          Save Changes
        </b-button>
        <b-button size="sm" variant="secondary" @click="cancel()">
          <template v-if="readOnly">
            Close
          </template>
          <template v-else>
            Cancel
          </template>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script lang="ts">
import _ from 'lodash'

import { Component, Prop, Watch, Mixins, Emit } from 'vue-property-decorator'
import LoadingText from '@/components/utility/loading-text.vue'
import EditorEditor from '@/components/editor/utility/editor-editor.vue'
import StatusBadge from '@/components/editor/status-badge.vue'
import { namespace } from 'vuex-class'
import { EditorSection } from '@/store/editor-editors/types'

import {
  Category,
  TPIException,
  EditBase,
} from '@/store/editor/types'

import {
  Model,
} from '@/store/editor-model/types'

import MxEditor from '@/mixins/editor'
import { Section, SectionTab, SectionTable } from '@/store/editor-section/types'
import { blankCategory, blankProductClass, blankManufacturingSeries, blankProduct } from '@/store/editor/constants'
import { blankModel } from '@/store/editor-model/constants'
import { Product } from '@/store/types'

const nsEditorModel = namespace('editor-model')
const nsEditorState = namespace('editor-state')

@Component({
  components: {
    'loading-text': LoadingText,
    'editor-editor': EditorEditor,
    'status-badge': StatusBadge,
  },
})
export default class extends Mixins(MxEditor) {
  public changed = false
  public valid = true
  public adding = false
  public comment = ''
  public editing = false
  public editingEntireModel = false
  public copying = false

  @Prop()
  public addingModel!: boolean

  @Prop()
  public editingModel!: boolean

  @Prop()
  public copyingModel!: boolean

  @Prop()
  public product!: Product

  @Prop()
  public section!: Section

  @Prop()
  public sectionTab!: SectionTab

  @Prop()
  public sectionTable!: SectionTable

  @Prop()
  public pcnCatalogNumber!: string

  @nsEditorState.Mutation('addRecentlySearchedPcn')
  public addRecentlySearchedPcn: any

  @nsEditorState.State('recentlySearchedPcns')
  public recentlySearchedPcns!: string[]

  @nsEditorModel.Action('loadActiveModel')
  public loadActiveModel: any

  @nsEditorModel.Action('deleteModel')
  public deleteModel: any

  @nsEditorModel.Action('findOrAddModel')
  public findOrAddModel: any

  @nsEditorModel.Action('getModelExists')
  public getModelExists: any

  public onChanged(changed: boolean) {
    this.changed = changed
    this.$emit('changed', this.changed)
  }

  public onValidated(valid: boolean) {
    this.valid = valid
    this.$emit('validated', this.valid)
  }

  public sectionMounted() {
    // this.isSectionMounted = true
  }

  @nsEditorModel.Getter('modelAssociationsValid')
  public modelAssociationsValid!: boolean

  @nsEditorModel.Getter('isErrored')
  public isErrored!: boolean

  @nsEditorModel.Mutation('resetActiveModel')
  public resetActiveModel: any

  @nsEditorModel.Action('updateModelSection')
  public updateModelSection!: any

  @nsEditorModel.Action('saveActiveModel')
  public saveActiveModel: any

  public activeModel: Model = blankModel

  public searchPcn = ''
  public associationInvalid = 'Please check that all associations are valid and that there is only one association per product.'
  public modelInvalid = 'Please check that all model information is properly entered.'
  public invalidFeedback = 'Catalog Number must be between 1 and 18 characters'

  public modelExist = false
  public copyModelNum = false

  get noProduct() {
    return !this.product
  }

  get copyPcnState() {
    if (this.modelExist) {
      return false
    }
    let validState: boolean | null = null
    validState = !this.searchPcn ? null : this.searchPcn.length > 0 && this.searchPcn.length <= 18
    if (validState === false) {
      this.invalidFeedback = 'Catalog Number must be between 1 and 18 characters'
    }
    return validState
  }

  get readOnly() {
    const { activeModel } = this

    if (activeModel.loading) return true

    return !(activeModel as Model).Created && !this.editingEntireModel
  }

  @Watch('addingModel')
  public addingModelChanged(val: boolean) {
    this.adding = val
    if (val && this.pcnCatalogNumber) {
      this.setEditorLoading({ value: true }).then(() => {
        this.searchPcn = this.pcnCatalogNumber // fake like we searched!
        this.findOrAddModelInternal(this.searchPcn).then((value: any) => {
          if (!value.Created) {
            this.toastError('The catalog number already exists, please add a new unique catalog number')
            // Close Add function in parent in reality is just setting the variable addingModel to false
            this.$emit('closeAdd', this.adding)
            return
          }

          this.searchPcn = ''
          this.setEditorLoading({ value: false }).then(() => {
            this.editing = true
            this.closeAdd()
          })
        })
      })
    }
  }

  @Watch('editingModel')
  public editingModelChanged(val: boolean) {
    const { pcnCatalogNumber } = this
    this.setEditorLoading({ value: true }).then(() => {
      this.findOrAddModelInternal(pcnCatalogNumber).then(() => {
        this.setEditorLoading({ value: false }).then(() => {
          this.editing = val
        })
      })
    })
  }

  @Watch('copyingModel')
  public copyingModelChanged(val: boolean) {
    this.copying = val
  }

  @Watch('searchPcn')
  public resetModelExist() {
    this.modelExist = false
  }

  public editModel() {
    this.editingEntireModel = true

    this.setEditorLoading({ value: true }).then(() => {
      this.setEditorLoading({ value: false }).then(() => {
        this.editing = true
      })
    })
  }

  public closeAdd() {
    this.adding = false
    this.searchPcn = ''
    this.$emit('closeAdd', this.adding)
  }

  @Emit('closeEdit')
  public closeEdit() {
    this.editing = false
    this.editingEntireModel = false
    this.changed = false
    return false
  }

  @Emit('closeCopy')
  public closeCopy() {
    this.searchPcn = ''
    this.copyModelNum = false
    return false
  }

  public findOrAddModelInternal(pcnCatalogNumber: string) {
    return new Promise(resolve => {
      const { noProduct, currentTopologyFull, categories, section, sectionTab, sectionTable } = this
      if (noProduct) {
        this.findOrAddModel({ pcnCatalogNumber }).then((model) => {
          this.activeModel = model
          resolve(model)
        })
      } else {
        const topology = currentTopologyFull

        const category: Category = categories.find(c => c.CategoryId === topology.category.CategoryId) || blankCategory
        const productClass = category.ProductClasses.find(pc => pc.ProductClassId === topology.productClass.ProductClassId) || blankProductClass
        const manufacturingSeries = productClass.ManufacturingSeries.find(ms => ms.ManufacturingSeriesId === topology.manufacturingSeries.ManufacturingSeriesId) || blankManufacturingSeries
        const product = manufacturingSeries.Products.find(p => p.ProductId === topology.product.ProductId) || blankProduct
        this.findOrAddModel({ pcnCatalogNumber, category, productClass, manufacturingSeries, product, section, sectionTab, sectionTable }).then((model) => {
          this.setEditorLoading({ value: false })
          this.activeModel = model
          resolve(model)
        })
      }
    })
  }

  public searchModelClick() {
    this.setEditorLoading({ value: true }).then(() => {
      this.addRecentlySearchedPcn(this.searchPcn)
      // TODO: pcnCatalogNumber not an "active" thing anymore?
      this.findOrAddModelInternal(this.searchPcn).then(() => {
        // this.$bvModal.hide('add-model')

        this.closeAdd()
        this.searchPcn = ''
        this.setEditorLoading({ value: false }).then(() => {
          this.editing = true
        })
      })
    })
    // this.$bvModal.show('model-details')
  }

  public copyClick() {
    const { pcnCatalogNumber, searchPcn, copyModelNum } = this
    this.setEditorLoading({ value: true }).then(() => {
      this.getModelExists({ pcnCatalogNumber: searchPcn }).then((flag: boolean) => {
        if (flag) {
          this.invalidFeedback = 'Specified Catalog Number already exists'
          this.modelExist = true
          this.setEditorLoading({ value: false })
        } else {
          // TODO: do thee actual thing!
          this.findOrAddModelInternal(pcnCatalogNumber).then((model: any) => {
            const newModel = _.assign({}, _.cloneDeep(model)) as Model
            newModel.PcnCatalogNumber = searchPcn?.toString().padStart(8, '0')
            newModel.Created = true
            newModel.Descriptions.forEach(element => { element.Created = true })
            newModel.DivisionModels.forEach(element => { element.Created = true })
            newModel.LabelDescriptions.forEach(element => { element.Created = true })
            newModel.ModelDimensions.forEach(element => { element.Created = true })
            newModel.Notes.forEach(element => { element.Created = true })

            newModel.ProductModels.forEach(productModel => {
              productModel.Created = true
              productModel.ProductModelId = -1

              productModel.SectionTableModels.forEach(sectionTableModel => {
                sectionTableModel.Created = true
                sectionTableModel.SectionTableModelId = -1
              })
            })

            if (!copyModelNum) {
              newModel.FingoodTpiMfgMod = ''
            }

            this.activeModel = newModel

            this.copying = false
            this.searchPcn = ''
            this.copyModelNum = false
            this.editing = true
            this.setEditorLoading({ value: false })
            this.closeCopy()
          })
        }
      })
    })
  }

  public saveChanges(obj: EditBase, editorSection: EditorSection) {
    return new Promise((resolve, reject) => {
      this.setEditorLoading({ value: true }).then(() => {
        // TODO: you weren't actually cloning anything here?
        // TODO: type: ModelAssociations
        // const associations: any = { PcnCatalogNumber: obj.PcnCatalogNumber, ChildAssociations: obj.ChildAssociations, ParentAssociations: obj.ParentAssociations }
        this.updateModelSection({ obj, editorSection, pcnCatalogNumber: obj.PcnCatalogNumber }).then(() => {
          this.toastSuccess((editorSection.DisplayName ?? 'Model') + ' saved succesfully.')
          this.findOrAddModelInternal(obj.PcnCatalogNumber).then(() => {
            this.setEditorLoading({ value: false }).then(() => {
            })
          })
        }).catch(err => {
          this.toastError('Error')
          reject(err)
        })
      })
    })
  }

  public saveOrUpdateModel() {
    const { activeModel, pcnCatalogNumber } = this
    return this.saveActiveModel({ activeModel, originalPcnCatalogNumber: pcnCatalogNumber }).then((model: Model) => {
      this.toastSuccess('Save Successful!')
      this.activeModel = model
      this.closeEdit()
      this.changed = false
    }).catch((exception: TPIException) => {
      this.toastError(exception.Message)
    })
  }

  public deleteModelInternal() {
    const { comment } = this

    return this.deleteModel({ model: this.activeModel, comment }).then((model: Model) => {
      this.resetActiveModel()
      this.comment = ''
      this.editing = false
      this.toastSuccess('Delete request sent Successful for ' + model.EntityName + ' ' + model.DisplayName)
    })

      .catch((exception: TPIException) => {
        this.toastError(exception.Message)
      })
  }

  public cancelEditSection() {
    this.loadActiveModel({ pcnCatalogNumber: this.pcnCatalogNumber }).then((model) => {
      this.activeModel = model
      this.changed = false
    })
  }
}
</script>
